import React from 'react';
import { RadioGroup } from '@headlessui/react';
import { motion } from 'framer-motion';

interface TierSelectorProps {
  selectedTier: string;
  onChange: (tier: string) => void;
}

const tiers = [
  { value: 'all', name: 'All Tiers', description: 'View all available solutions' },
  { value: 'bronze', name: 'Bronze', description: 'Essential solutions', color: 'bg-amber-600' },
  { value: 'silver', name: 'Silver', description: 'Enhanced solutions', color: 'bg-slate-400' },
  { value: 'gold', name: 'Gold', description: 'Premier solutions', color: 'bg-accent dark:bg-dark-accent' },
];

const TierSelector: React.FC<TierSelectorProps> = ({ selectedTier, onChange }) => {
  return (
    <div className="w-full py-6">
      <div className="max-w-2xl mx-auto">
        <RadioGroup value={selectedTier} onChange={onChange}>
          <RadioGroup.Label className="sr-only">Service tier</RadioGroup.Label>
          <div className="grid grid-cols-1 gap-y-4 sm:grid-cols-2 sm:gap-x-4 md:grid-cols-4">
            {tiers.map((tier) => (
              <RadioGroup.Option
                key={tier.value}
                value={tier.value}
                className={({ active, checked }) =>
                  `${
                    active ? 'ring-2 ring-offset-2 ring-ring dark:ring-offset-dark-background dark:ring-dark-ring' : ''
                  } ${
                    checked
                      ? 'bg-primary border-transparent text-primary-foreground dark:bg-dark-accent dark:text-dark-accent-foreground'
                      : 'bg-card border-border text-card-foreground dark:bg-dark-card dark:border-dark-border dark:text-dark-card-foreground'
                  } relative rounded-lg shadow-md border p-4 cursor-pointer flex focus:outline-none transition-colors duration-150`
                }
              >
                {({ active, checked }) => (
                  <>
                    <div className="flex items-center justify-between w-full">
                      <div className="flex items-center">
                        <div className="text-sm">
                          <RadioGroup.Label
                            as="div"
                            className={`font-medium ${
                              checked ? 'text-primary-foreground dark:text-dark-accent-foreground' : 'text-card-foreground dark:text-dark-card-foreground'
                            }`}
                          >
                            <div className="flex items-center">
                              {tier.value !== 'all' && (
                                <span className={`inline-block w-3 h-3 rounded-full mr-2 ${tier.color}`}></span>
                              )}
                              {tier.name}
                            </div>
                          </RadioGroup.Label>
                          <RadioGroup.Description
                            as="span"
                            className={`inline ${
                              checked ? 'text-white dark:text-procreative-green' : 'text-gray-500 dark:text-dark-text-secondary'
                            }`}
                          >
                            <span>{tier.description}</span>
                          </RadioGroup.Description>
                        </div>
                      </div>
                      {checked && (
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          className="shrink-0 text-white"
                        >
                          <svg className="h-6 w-6" viewBox="0 0 24 24" fill="none">
                            <circle cx={12} cy={12} r={12} fill="#fff" fillOpacity="0.2" />
                            <path
                              d="M7 13l3 3 7-7"
                              stroke="#fff"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </motion.div>
                      )}
                    </div>
                  </>
                )}
              </RadioGroup.Option>
            ))}
          </div>
        </RadioGroup>
      </div>
    </div>
  );
};

export default TierSelector;
